
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type CourseDeleteButtonProps = {
  courseId: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onDeleted?: () => void;
};

export function CourseDeleteButton({ 
  courseId, 
  isOpen, 
  onOpenChange,
  onDeleted 
}: CourseDeleteButtonProps) {
  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('course_offerings')
        .delete()
        .eq('id', courseId);

      if (error) throw error;

      toast.success("Course deleted successfully");

      if (onDeleted) {
        onDeleted();
      } else {
        navigate('/admin/courses');
      }
    } catch (error) {
      toast.error("Failed to delete course");
    }
  };

  return (
    <>
      <button 
        className="text-destructive underline text-sm hover:text-destructive/80" 
        onClick={() => onOpenChange(true)}
      >
        Delete Course
      </button>

      <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the course
              and remove it from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                onOpenChange(false);
                handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
